// Cache local în IndexedDB: aplicația pornește instant și merge offline.
// NU este sursa adevărului — serverul este. Aici ținem doar o copie + coada de operații netrimise.
import { openDB, type IDBPDatabase } from 'idb';
import type { EntityName, Op } from '../../shared/types';

export const ENTITY_NAMES: EntityName[] = ['households', 'members', 'works', 'copies', 'reading', 'loans'];

interface Schema {
  meta: { key: string; value: unknown };
  outbox: { key: number; value: { seq?: number; op: Op; queued_at: number } };
}

let dbp: Promise<IDBPDatabase<any>> | null = null;

export function localDb() {
  if (!dbp) {
    dbp = openDB<any>('casa-cartilor-v10', 1, {
      upgrade(db) {
        for (const e of ENTITY_NAMES) db.createObjectStore(e, { keyPath: 'id' });
        db.createObjectStore('meta');
        db.createObjectStore('outbox', { keyPath: 'seq', autoIncrement: true });
      }
    });
  }
  return dbp;
}

export async function loadAll(): Promise<{ rows: Record<EntityName, any[]>; cursor: number; me: any; outbox: Array<{ seq: number; op: Op }> }> {
  const db = await localDb();
  const rows = {} as Record<EntityName, any[]>;
  for (const e of ENTITY_NAMES) rows[e] = await db.getAll(e);
  const cursor = (await db.get('meta', 'cursor')) as number || 0;
  const me = await db.get('meta', 'me');
  const outbox = await db.getAll('outbox');
  return { rows, cursor, me, outbox };
}

export async function saveRows(rowsByEntity: Partial<Record<EntityName, any[]>>, cursor: number, me: unknown, full: boolean) {
  const db = await localDb();
  const tx = db.transaction([...ENTITY_NAMES, 'meta'], 'readwrite');
  for (const e of ENTITY_NAMES) {
    const store = tx.objectStore(e);
    if (full) await store.clear();
    for (const r of rowsByEntity[e] || []) {
      if (r.deleted_at) await store.delete(r.id);
      else await store.put(r);
    }
  }
  await tx.objectStore('meta').put(cursor, 'cursor');
  if (me) await tx.objectStore('meta').put(me, 'me');
  await tx.done;
}

export async function enqueue(op: Op): Promise<number> {
  const db = await localDb();
  return db.add('outbox', { op, queued_at: Date.now() }) as Promise<number>;
}

export async function removeFromOutbox(seqs: number[]) {
  const db = await localDb();
  const tx = db.transaction('outbox', 'readwrite');
  for (const s of seqs) await tx.store.delete(s);
  await tx.done;
}

export async function resetLocal() {
  const db = await localDb();
  const tx = db.transaction([...ENTITY_NAMES, 'meta'], 'readwrite');
  for (const e of ENTITY_NAMES) await tx.objectStore(e).clear();
  await tx.objectStore('meta').clear();
  await tx.done;
}
